/**
 * Check if a product is available based on time ranges in ProductCategory
 * @param {Object} productCategory - The ProductCategory object with time fields
 * @returns {boolean} - true if product is available, false otherwise
 */
export const isProductAvailable = (productCategory) => {
  // If no ProductCategory or no time fields, assume always available
  if (!productCategory || !productCategory.from_time_1 || !productCategory.to_time_1) {
    return true;
  }

  const currentTime = new Date();
  const currentTimeString = currentTime.toTimeString().slice(0, 8); // Get HH:MM:SS format
  
  // Helper function to convert time string to minutes for easier comparison
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const currentMinutes = timeToMinutes(currentTimeString);

  // Check first time range (from_time_1 to to_time_1)
  const fromTime1 = timeToMinutes(productCategory.from_time_1);
  const toTime1 = timeToMinutes(productCategory.to_time_1);
  
  // If opening and closing times are the same, consider it always open
  const isInFirstRange = fromTime1 === toTime1 ? true : (currentMinutes >= fromTime1 && currentMinutes <= toTime1);

  // Check second time range if it exists (from_time_2 to to_time_2)
  let isInSecondRange = false;
  if (productCategory.from_time_2 && productCategory.to_time_2) {
    const fromTime2 = timeToMinutes(productCategory.from_time_2);
    const toTime2 = timeToMinutes(productCategory.to_time_2);
    // If opening and closing times are the same, consider it always open
    isInSecondRange = fromTime2 === toTime2 ? true : (currentMinutes >= fromTime2 && currentMinutes <= toTime2);
  }

  // Product is available if it's in either time range
  return isInFirstRange || isInSecondRange;
};

/**
 * Get the next available time for a product
 * @param {Object} productCategory - The ProductCategory object with time fields
 * @returns {string|null} - Next available time or null if always available
 */
export const getNextAvailableTime = (productCategory) => {
  if (!productCategory || !productCategory.from_time_1 || !productCategory.to_time_1) {
    return null; // Always available
  }

  const currentTime = new Date();
  const currentTimeString = currentTime.toTimeString().slice(0, 8);
  
  const timeToMinutes = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const currentMinutes = timeToMinutes(currentTimeString);
  const fromTime1 = timeToMinutes(productCategory.from_time_1);
  const fromTime2 = productCategory.from_time_2 ? timeToMinutes(productCategory.from_time_2) : null;

  // Find the next available time
  if (currentMinutes < fromTime1) {
    return productCategory.from_time_1;
  } else if (fromTime2 && currentMinutes < fromTime2) {
    return productCategory.from_time_2;
  } else {
    // Next day's first time
    return productCategory.from_time_1;
  }
};

/**
 * Format time for display
 * @param {string} timeString - Time in HH:MM:SS format
 * @returns {string} - Formatted time for display
 */
export const formatTimeForDisplay = (timeString) => {
  if (!timeString) return '';
  
  const [hours, minutes] = timeString.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

